import { useState } from 'react';
// @mui
import { useTheme } from '@mui/material/styles';
import { Avatar, Checkbox, TableRow, TableCell, Typography, MenuItem } from '@mui/material';
// @types
// components
import { useNavigate } from 'react-router';
import { TableMoreMenu } from 'src/components/table';
import Iconify from 'src/components/Iconify';
import Label from 'src/components/Label';
import { PromoCodeType } from 'src/@types/PromoCode';

// ----------------------------------------------------------------------

type Props = {
  row: PromoCodeType;
  selected: boolean;
  // Column show/hide: ids of the columns to render. Undefined = show all.
  visibleColumns?: string[];
  // Deleting a promo code is super-admin-only (mirrors the BE @Auth). When
  // false, the Delete action is hidden — the row still offers Edit.
  canDelete?: boolean;
  // Maps to render the Scope column as names (rows carry ids).
  diplomaMap?: Record<string, string>;
  batchMap?: Record<string, string>;
  onEditRow: VoidFunction;
  onSelectRow: VoidFunction;
  onDeleteRow: VoidFunction;
};

export default function PromoCodeTableRow({
  row,
  selected,
  visibleColumns,
  canDelete = true,
  diplomaMap,
  batchMap,
  onEditRow,
  onSelectRow,
  onDeleteRow,
}: Props) {
  const theme = useTheme();

  const { amount, code, usageLimit, usageCount, source, partnerName, isActive, expiryDate } = row;
  const showCol = (id: string) => !visibleColumns || visibleColumns.includes(id);

  // Human-readable scope: Global / program title / "program · batch".
  // Accepts a raw id OR a populated object for either field, and prefers a
  // populated title/name before falling back to the id→name maps.
  const rawDiploma: any = (row as any).diploma;
  const rawBatch: any = (row as any).batchId;
  const diplomaId = rawDiploma ? String(rawDiploma?._id ?? rawDiploma) : '';
  const batchId = rawBatch ? String(rawBatch?._id ?? rawBatch) : '';
  // Prefer the names the BE resolves on the row (works for every role); fall
  // back to the client-side maps / a populated object, then a generic label.
  const diplomaName =
    (row as any).diplomaName ||
    rawDiploma?.title ||
    rawDiploma?.name ||
    diplomaMap?.[diplomaId] ||
    'Program';
  const batchName = (row as any).batchName || rawBatch?.name || batchMap?.[batchId] || 'Batch';
  const scopeLabel = !diplomaId && !batchId
    ? 'Global'
    : batchId
    ? `${diplomaName} · ${batchName}`
    : diplomaName;

  // An active code past its expiry date is effectively inactive — surface that.
  const isExpired = !!expiryDate && new Date(expiryDate) < new Date();
  const statusLabel = isExpired ? 'Expired' : isActive ? 'Active' : 'Inactive';
  const statusColor: 'success' | 'warning' | 'default' = isExpired
    ? 'warning'
    : isActive
    ? 'success'
    : 'default';

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };
  let navigate = useNavigate();
  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      {showCol('Code') && (
        <TableCell>
          <Typography variant="subtitle2" noWrap>
            {code}
          </Typography>
        </TableCell>
      )}

      {showCol('amount') && <TableCell align="left"> {`${amount} %`}</TableCell>}
      {showCol('usageLimit') && <TableCell align="left">{usageLimit}</TableCell>}
      {showCol('usageCount') && <TableCell align="left">{usageCount}</TableCell>}
      {showCol('source') && (
        <TableCell align="left">
          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
            {source || 'admin'}
          </Typography>
          {source === 'partner' && partnerName ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {partnerName}
            </Typography>
          ) : null}
        </TableCell>
      )}
      {showCol('scope') && (
        <TableCell align="left">
          <Typography
            variant="body2"
            noWrap
            title={scopeLabel}
            sx={{ color: !diplomaId && !batchId ? 'text.secondary' : 'text.primary' }}
          >
            {scopeLabel}
          </Typography>
        </TableCell>
      )}
      {showCol('status') && (
        <TableCell align="left">
          <Label color={statusColor}>{statusLabel}</Label>
        </TableCell>
      )}

      <TableCell align="right">
        <TableMoreMenu
          open={openMenu}
          onOpen={handleOpenMenu}
          onClose={handleCloseMenu}
          actions={
            <>
              <MenuItem
                onClick={() => {
                  onEditRow();
                  handleCloseMenu();
                }}
              >
                <Iconify icon={'eva:edit-fill'} />
                Edit
              </MenuItem>
              {canDelete && (
                <MenuItem
                  onClick={() => {
                    onDeleteRow();
                    handleCloseMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <Iconify icon={'eva:trash-2-outline'} />
                  Delete
                </MenuItem>
              )}
            </>
          }
        />
      </TableCell>
    </TableRow>
  );
}
